(() => {
  if (window.mmProConfiguratorLoaded) return;
  window.mmProConfiguratorLoaded = true;

  const selector = '[data-mm-pro-configurator]';

  function initialise(root) {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';
    root.classList.add('mm-pro-configurator--js');

    const panels = [...root.querySelectorAll('[data-config-panel]')];
    const stepLinks = [...root.querySelectorAll('[data-config-step-link]')];
    const previousButton = root.querySelector('[data-config-prev]');
    const nextButton = root.querySelector('[data-config-next]');
    const summaryButton = root.querySelector('[data-config-summary-button]');
    const counter = root.querySelector('[data-config-counter]');
    const scroller = root.querySelector('[data-config-scroller]');
    const viewport = root.querySelector('.mm-pro-configurator__viewport');
    const summary = root.querySelector('[data-config-summary]');
    const contactForm = root.querySelector('.mm-pro-configurator__contact-form');
    const contactResult = root.querySelector('[data-config-form-result]');
    const variantsElement = root.querySelector('[data-config-variants]');
    let variants = [];
    try {
      variants = JSON.parse(variantsElement?.textContent || '[]');
    } catch {
      variants = [];
    }
    const isDesignMode = root.dataset.designMode === 'true';
    let currentStep = 1;
    let returnFocus = null;
    let previousOverflow = '';
    let inertSiblings = [];
    let resultPending = Boolean(contactResult);

    function selection(step) {
      const panel = panels.find((item) => Number(item.dataset.configPanel) === step);
      const checked = panel?.querySelector('[data-config-choice]:checked');
      return checked?.dataset.choiceLabel || '';
    }

    function detail(step) {
      return root.querySelector(`[data-config-detail="${step}"]`)?.value.trim() || '';
    }

    function variantSelection(step) {
      return panels[step - 1]?.querySelector('[data-config-variant-value]:checked');
    }

    function matchingVariant() {
      const motor = variantSelection(4);
      const stroke = variantSelection(5);
      if (!motor || !stroke) return null;
      const motorIndex = Number(motor.dataset.configOptionPosition) - 1;
      const strokeIndex = Number(stroke.dataset.configOptionPosition) - 1;
      if (motorIndex < 0 || strokeIndex < 0 || motorIndex === strokeIndex) return null;
      return variants.find((variant) => variant.options?.[motorIndex] === motor.value &&
        variant.options?.[strokeIndex] === stroke.value) || null;
    }

    function refreshAvailability() {
      const motor = variantSelection(4);
      let unavailableCount = 0;
      panels[4]?.querySelectorAll('[data-config-variant-value]').forEach((stroke) => {
        const strokeIndex = Number(stroke.dataset.configOptionPosition) - 1;
        const motorIndex = Number(motor?.dataset.configOptionPosition) - 1;
        const exists = variants.some((variant) => variant.options?.[strokeIndex] === stroke.value &&
          (!motor || variant.options?.[motorIndex] === motor.value));
        stroke.disabled = !exists;
        stroke.closest('.mm-pro-configurator__choice')?.classList.toggle('is-unavailable', !exists);
        const unavailableNote = stroke.closest('.mm-pro-configurator__choice')?.querySelector('[data-config-unavailable-note]');
        if (unavailableNote) unavailableNote.hidden = exists;
        if (!exists) {
          unavailableCount += 1;
          stroke.checked = false;
        }
      });
      const message = panels[4]?.querySelector('[data-config-availability]');
      if (message && panels[4].querySelector('[data-config-variant-value]')) {
        message.hidden = unavailableCount === 0;
        message.textContent = root.dataset.combinationWarning;
      }
    }

    function refreshActions() {
      const complete = Array.from({ length: 5 }, (_, index) => selection(index + 1)).every(Boolean) && Boolean(matchingVariant());
      nextButton.disabled = currentStep <= 5 && !selection(currentStep);
      if (currentStep === 5 && !matchingVariant()) nextButton.disabled = true;
      summaryButton.disabled = !complete;
    }

    function refreshStepValues() {
      stepLinks.forEach((link) => {
        const step = Number(link.dataset.configStepLink);
        const value = link.querySelector('[data-config-step-value]');
        if (value) value.textContent = selection(step) || root.dataset.labelUnanswered;
      });
      refreshActions();
    }

    function renderSummary() {
      summary.replaceChildren();
      for (let step = 1; step <= 5; step += 1) {
        const panel = panels[step - 1];
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = panel.dataset.stepTitle;
        description.textContent = [selection(step) || root.dataset.labelUnanswered, detail(step)]
          .filter(Boolean)
          .join(' · ');
        summary.append(term, description);
      }
    }

    function setStep(step, focusHeading = true) {
      currentStep = Math.min(7, Math.max(1, step));
      panels.forEach((panel) => {
        panel.hidden = Number(panel.dataset.configPanel) !== currentStep;
      });
      stepLinks.forEach((link) => {
        if (Number(link.dataset.configStepLink) === currentStep) {
          link.setAttribute('aria-current', 'step');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      if (currentStep === 6) renderSummary();
      refreshStepValues();

      previousButton.disabled = currentStep === 1;
      nextButton.hidden = currentStep >= 5;
      summaryButton.hidden = currentStep !== 5 && currentStep !== 6;
      summaryButton.querySelector('[data-config-primary-label]').textContent = currentStep === 6
        ? root.dataset.labelContact : root.dataset.labelSummary;
      nextButton.textContent = root.dataset.labelNext;
      counter.textContent = currentStep <= 5
        ? `${root.dataset.stepWord} ${currentStep} / 5`
        : panels[currentStep - 1].querySelector('h3')?.textContent || '';
      refreshActions();

      requestAnimationFrame(() => {
        if (!root.isConnected || root.dataset.open !== 'true') return;
        if (window.matchMedia('(max-width: 999px)').matches) {
          const heading = panels[currentStep - 1].querySelector('h3');
          if (!heading) return;
          const top = viewport.scrollTop + heading.getBoundingClientRect().top - viewport.getBoundingClientRect().top - 135;
          viewport.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        } else {
          scroller.scrollTo({ top: 0, behavior: 'auto' });
        }
      });
      if (focusHeading) {
        const heading = panels[currentStep - 1].querySelector('h3');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      }
    }

    function makeBackgroundInert() {
      let child = root;
      while (child.parentElement) {
        const parent = child.parentElement;
        [...parent.children].forEach((sibling) => {
          if (sibling !== child) {
            inertSiblings.push([sibling, sibling.inert]);
            sibling.inert = true;
          }
        });
        if (parent === document.body) break;
        child = parent;
      }
    }

    function open() {
      if (isDesignMode || root.dataset.open === 'true') return;
      returnFocus = document.activeElement;
      previousOverflow = document.body.style.overflow;
      root.dataset.open = 'true';
      root.setAttribute('role', 'dialog');
      root.setAttribute('aria-modal', 'true');
      document.body.style.overflow = 'hidden';
      makeBackgroundInert();
      setStep(contactResult ? 7 : currentStep, false);
      (contactResult || root).focus({ preventScroll: true });
      resultPending = false;
    }

    function close() {
      if (root.dataset.open !== 'true') return;
      delete root.dataset.open;
      root.removeAttribute('role');
      root.removeAttribute('aria-modal');
      document.body.style.overflow = previousOverflow;
      inertSiblings.forEach(([sibling, wasInert]) => { sibling.inert = wasInert; });
      inertSiblings = [];
      if (location.hash === `#${root.id}`) {
        history.replaceState(null, '', `${location.pathname}${location.search}`);
      }
      const fallback = document.querySelector(`a[href="#${root.id}"]`);
      const canReturnFocus = returnFocus && returnFocus.isConnected &&
        returnFocus !== document.body && returnFocus !== document.documentElement &&
        !root.contains(returnFocus);
      const target = canReturnFocus ? returnFocus : fallback;
      target?.focus({ preventScroll: true });
    }

    function syncFromLocation() {
      if (location.hash === `#${root.id}` || resultPending) open();
      else close();
    }

    function openFromLink(event) {
      const link = event.target instanceof Element ? event.target.closest(`a[href="#${root.id}"]`) : null;
      if (!link || isDesignMode) return;
      event.preventDefault();
      history.replaceState(null, '', `${location.pathname}${location.search}#${root.id}`);
      open();
    }

    function reset() {
      root.querySelectorAll('[data-config-choice]').forEach((choice) => { choice.checked = false; });
      for (let step = 1; step <= 3; step += 1) {
        const choices = panels[step - 1]?.querySelectorAll('[data-config-choice]');
        if (choices?.length === 1) choices[0].checked = true;
      }
      root.querySelectorAll('[data-config-detail], [data-config-boat], [data-config-note]')
        .forEach((field) => { field.value = ''; });
      refreshAvailability();
      refreshStepValues();
      setStep(1);
    }

    function prepareContactBody() {
      const lines = [`Produit : ${root.dataset.productTitle}`, 'Demande de qualification technique'];
      for (let step = 1; step <= 5; step += 1) {
        const panel = panels[step - 1];
        lines.push(`${panel.dataset.stepTitle} : ${selection(step) || root.dataset.labelUnanswered}`);
        if (detail(step)) lines.push(`Précision : ${detail(step)}`);
      }
      const variant = matchingVariant();
      if (variant) {
        lines.push(`Variante Shopify : ${variant.id}`);
      }
      const boat = root.querySelector('[data-config-boat]')?.value.trim();
      const note = root.querySelector('[data-config-note]')?.value.trim();
      if (boat) lines.push(`Bateau : ${boat}`);
      if (note) lines.push(`Message : ${note}`);
      contactForm.querySelector('[data-config-body]').value = lines.join('\n');
    }

    root.querySelectorAll('[data-config-choice]').forEach((choice) => {
      choice.addEventListener('change', () => {
        refreshAvailability();
        refreshStepValues();
      });
    });
    root.querySelector('[data-config-reset]')?.addEventListener('click', reset);
    previousButton?.addEventListener('click', () => setStep(currentStep - 1));
    nextButton?.addEventListener('click', () => setStep(currentStep + 1));
    summaryButton?.addEventListener('click', () => setStep(currentStep + 1));
    stepLinks.forEach((link) => {
      link.addEventListener('click', () => setStep(Number(link.dataset.configStepLink)));
    });
    root.querySelectorAll('[data-config-close]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!isDesignMode) {
          event.preventDefault();
          close();
        }
      });
    });
    contactForm?.addEventListener('submit', (event) => {
      if (!matchingVariant()) {
        event.preventDefault();
        setStep(5);
        return;
      }
      prepareContactBody();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !isDesignMode) {
        event.preventDefault();
        close();
      }
      if (event.key !== 'Tab' || root.dataset.open !== 'true') return;
      const focusable = [...root.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled):not([type="hidden"]), textarea:not([hidden])')]
        .filter((element) => element.getClientRects().length && !element.closest('[hidden]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('hashchange', syncFromLocation);
    document.addEventListener('click', openFromLink);
    const unload = (event) => {
      if (!event.target.contains(root)) return;
      close();
      window.removeEventListener('hashchange', syncFromLocation);
      document.removeEventListener('click', openFromLink);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
    viewport.addEventListener('scroll', () => {
      root.dataset.mobileScrolled = viewport.scrollTop >= 400 ? 'true' : 'false';
    }, { passive: true });
    for (let step = 1; step <= 3; step += 1) {
      const choices = panels[step - 1]?.querySelectorAll('[data-config-choice]');
      if (choices?.length === 1) choices[0].checked = true;
    }
    refreshAvailability();
    refreshStepValues();
    if (contactResult) setStep(7, false);
    syncFromLocation();
  }

  function initialiseAll(scope = document) {
    scope.querySelectorAll(selector).forEach(initialise);
  }

  initialiseAll();
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
